import styled, { css, keyframes } from "styled-components";
import { colors, font, radius, shadow } from "../../../styles/theme/tokens";

// Стилі блоків уроку 2+: choose, video, grammar, unscramble, write (сцена 1280×720)

const rise = keyframes`
  from { opacity: 0; transform: translateY(12px); }
  to { opacity: 1; transform: translateY(0); }
`;

const stateColors = (state) =>
  state === "ok"
    ? css`border-color: ${colors.success}; background: ${colors.successSoft}; color: ${colors.success};`
    : state === "bad"
    ? css`border-color: ${colors.danger}; background: ${colors.dangerSoft}; color: ${colors.danger};`
    : state === "late"
    ? css`border-color: ${colors.amberDeep}; background: ${colors.amberSoft}; color: ${colors.amberDeep};`
    : state === "tried"
    ? css`border-style: dashed; border-color: ${colors.danger}; color: ${colors.danger}; text-decoration: line-through;`
    : "";

export const Mark = styled.b`
  color: ${colors.brandDeep};
`;

// ---------- choose ----------
export const ChooseWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  height: 100%;
  animation: ${rise} 0.4s ease-out;
`;

export const ChooseImages = styled.div`
  display: grid;
  grid-template-columns: repeat(${(props) => props.$count}, minmax(0, 1fr));
  gap: 16px;
  min-height: 0;
  max-height: ${(props) => (props.$thumbs ? "80px" : props.$big ? "230px" : props.$compact ? "200px" : "340px")};
  margin-bottom: ${(props) => (props.$thumbs || props.$big ? "12px" : "0")};
  flex: ${(props) => (props.$thumbs || props.$big ? "none" : "1")};
  height: ${(props) => (props.$thumbs ? "80px" : props.$big ? "230px" : "auto")};
`;

export const ChooseImage = styled.figure`
  position: relative;
  margin: 0;
  min-height: 0;
  border-radius: ${radius.lg};
  overflow: hidden;
  background: ${colors.surface};
  box-shadow: ${shadow.card};

  img {
    width: 100%;
    height: 100%;
    object-fit: ${(props) => (props.$contain ? "contain" : "cover")};
  }

  figcaption {
    position: absolute;
    top: 10px;
    left: 10px;
    display: grid;
    place-items: center;
    min-width: 36px;
    height: 36px;
    padding: 0 8px;
    border-radius: 18px;
    font-size: 20px;
    font-weight: 800;
    color: ${colors.ink};
    background: ${colors.surface};
    box-shadow: ${shadow.card};
  }
`;

export const Columns = styled.div`
  display: grid;
  grid-template-columns: repeat(${(props) => props.$count}, minmax(0, 1fr));
  gap: 28px;
`;

export const ChooseLines = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${(props) => (props.$dense ? "2px" : props.$compact ? "8px" : "14px")};
  font-size: ${(props) => (props.$dense ? "19px" : props.$compact ? "22px" : "27px")};

  ${(props) => props.$dense && css`
    p {
      line-height: 1.45;
    }
    button {
      padding: 0 12px;
    }
  `}
`;

export const ChooseHeading = styled.h3`
  margin-top: 4px;
  font-size: 18px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: ${colors.muted};
`;

export const ChooseLine = styled.p`
  display: flex;
  gap: 12px;
  font-weight: 600;
  line-height: 1.7;
`;

export const Speaker = styled.span`
  flex-shrink: 0;
  min-width: 90px;
  font-weight: 800;
  color: ${colors.brandDeep};
`;

export const Options = styled.span`
  display: inline-flex;
  gap: 6px;
  margin: 0 4px;
  vertical-align: middle;
`;

export const Option = styled.button`
  padding: 2px 14px;
  border-radius: ${radius.pill};
  border: 2px solid ${(props) => (props.$picked ? colors.brand : colors.line)};
  font-family: ${font.text};
  font-size: inherit;
  font-weight: 700;
  line-height: 1.4;
  color: ${colors.ink};
  background: ${(props) => (props.$picked ? colors.brandSoft : colors.surface)};
  cursor: pointer;
  ${(props) => stateColors(props.$state)}
`;

// ---------- video ----------
export const VideoLayout = styled.div`
  display: grid;
  grid-template-columns: ${(props) => (props.$wide ? "minmax(0, 1fr) minmax(0, 1.3fr)" : "minmax(0, 1.25fr) minmax(0, 1fr)")};
  align-items: start;
  gap: 32px;
  height: 100%;
  animation: ${rise} 0.4s ease-out;
`;

export const Video = styled.video`
  width: 100%;
  max-height: 100%;
  border-radius: ${radius.xl};
  background: #000000;
  box-shadow: ${shadow.card};
`;

export const VideoSide = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 18px;
`;

// ---------- reading ----------
export const ReadingLayout = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1.1fr) minmax(0, 1fr);
  grid-template-rows: minmax(0, 1fr); /* довгий текст / пост прокручується, а не вилазить за слайд */
  gap: 32px;
  height: 100%;
  animation: ${rise} 0.4s ease-out;
`;

export const Article = styled.article`
  overflow-y: auto;
  padding: 22px 26px;
  border-radius: ${radius.lg};
  background: ${colors.surface};
  box-shadow: ${shadow.card};

  h3 {
    margin-bottom: 10px;
    font-size: 26px;
    font-weight: 800;
    color: ${colors.brandDeep};
  }

  p {
    font-size: ${(props) => (props.$compact ? "17px" : "19px")};
    font-weight: 500;
    line-height: 1.55;
  }

  p + p {
    margin-top: 12px;
  }
`;

export const ArticleFigure = styled.figure`
  margin: 14px 0;

  img {
    display: block;
    width: 100%;
    max-height: ${(props) => (props.$tall ? "380px" : "260px")};
    object-fit: ${(props) => (props.$tall ? "contain" : "cover")};
    border-radius: ${radius.md};
  }

  figcaption {
    margin-top: 6px;
    font-size: 14px;
    font-style: italic;
    color: ${colors.muted};
  }
`;

export const ReadingSide = styled.div`
  overflow-y: auto;
`;

// ---------- grammar ----------
export const GrammarLayout = styled.div`
  display: flex;
  flex-direction: column;
  gap: 18px;
  height: 100%;
  animation: ${rise} 0.4s ease-out;
`;

export const Tables = styled.div`
  display: grid;
  grid-template-columns: repeat(${(props) => props.$cols}, minmax(0, 1fr));
  gap: 18px 24px;
`;

export const Table = styled.div`
  border-radius: ${radius.lg};
  overflow: hidden;
  background: ${colors.surface};
  box-shadow: ${shadow.card};

  h3 {
    padding: 8px 16px;
    font-size: 18px;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: #FFFFFF;
    background: ${colors.brandDeep};
  }

  table {
    width: 100%;
    border-collapse: collapse;
    font-size: 23px;
    font-weight: 600;
  }

  td {
    padding: 6px 16px;
    border-top: 1px solid ${colors.line};
  }
`;

export const Notes = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px 14px;
`;

export const Note = styled.p`
  padding: 8px 16px;
  border-radius: ${radius.md};
  font-size: 21px;
  font-weight: 600;
  background: ${colors.amberSoft};
  border-left: 5px solid ${colors.amber};
`;

// ---------- unscramble ----------
export const Scramble = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  animation: ${rise} 0.4s ease-out;
`;

export const ScrambleRow = styled.div`
  display: grid;
  grid-template-columns: 28px minmax(0, 1.1fr) minmax(0, 1fr);
  align-items: center;
  gap: 14px;
  font-size: 22px;

  > b {
    color: ${colors.brandDeep};
  }
`;

export const Built = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  min-height: 44px;
  padding: 4px 10px;
  border-radius: ${radius.md};
  border: 2px solid ${colors.line};
  background: ${colors.surface};
  ${(props) => stateColors(props.$state)}
`;

export const Pool = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

export const Word = styled.button`
  padding: 3px 12px;
  border-radius: ${radius.pill};
  font-family: ${font.text};
  font-size: 21px;
  font-weight: 700;
  color: ${colors.brandDeep};
  background: ${colors.brandSoft};
  cursor: pointer;
  visibility: ${(props) => (props.$hidden ? "hidden" : "visible")};
`;

export const End = styled.span`
  font-weight: 800;
`;

// ---------- write ----------
export const WriteList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${(props) => (props.$compact ? "8px" : "14px")};
  animation: ${rise} 0.4s ease-out;
`;

export const WriteRow = styled.div`
  display: grid;
  grid-template-columns: ${(props) => (props.$long ? "minmax(0, 1fr)" : `${props.$speaker ? "90px " : ""}minmax(0, 1fr) minmax(0, 1.15fr)`)};
  align-items: ${(props) => (props.$long ? "stretch" : "center")};
  gap: 14px;
  font-size: ${(props) => (props.$compact ? "20px" : "24px")};
`;

// основний текст завдання — чорний, читабельний (раніше сірий)
export const Prompt = styled.span`
  font-weight: 600;
  color: ${colors.ink};
`;

// Рядок-приклад «речення → як переписали» над полями (data.example)
export const WriteExample = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1.15fr);
  align-items: center;
  gap: 14px;
  padding: 4px 12px;
  border-radius: ${radius.md};
  font-size: ${(props) => (props.$compact ? "20px" : "24px")};
  background: ${colors.amberSoft};

  span {
    font-weight: 600;
    color: ${colors.ink};
  }
  b {
    padding: 4px 12px;
    border-bottom: 3px solid ${colors.amber};
    border-radius: 6px 6px 0 0;
    font-weight: 800;
    color: ${colors.ink};
    background: #ffffff;
  }
  em {
    display: block;
    font-size: 13px;
    font-style: normal;
    font-weight: 800;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: ${colors.amberDeep};
  }
`;

// textarea: текст переноситься по рядках (не «в один рядок»), довгий текст — з прокруткою
export const WriteInput = styled.textarea`
  display: block;
  width: 100%;
  min-height: ${(props) => (props.$long ? "300px" : "auto")};
  max-height: ${(props) => (props.$long ? "380px" : "6.5em")};
  padding: ${(props) => (props.$long ? "14px 16px" : "6px 12px")};
  resize: none;
  overflow-y: auto;
  line-height: 1.35;
  white-space: pre-wrap;
  overflow-wrap: break-word;
  border: none;
  border-bottom: 3px solid ${colors.brand};
  border-radius: 6px 6px 0 0;
  font-family: ${font.text};
  font-size: inherit;
  font-weight: 700;
  color: ${colors.ink};
  background: ${colors.brandSoft};
  outline: none;

  /* приклад-підказка: явно сірий курсив, щоб не здавалося, що хтось уже написав */
  &::placeholder {
    font-weight: 500;
    font-style: italic;
    color: ${colors.muted};
    opacity: 0.75;
  }
  ${(props) =>
    props.$state === "ok"
      ? css`border-bottom-color: ${colors.success}; background: ${colors.successSoft}; color: ${colors.success};`
      : props.$state === "bad"
      ? css`border-bottom-color: ${colors.danger}; background: ${colors.dangerSoft};`
      : ""}
`;

export const WriteWithImage = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 0.9fr) minmax(0, 1.1fr);
  gap: 28px;
  align-items: center;
  height: 100%;

  figure {
    height: 100%;
    max-height: 460px;
  }

  /* у вузькій колонці — підказка над полем */
  > div:last-child > div {
    grid-template-columns: minmax(0, 1fr);
    gap: 4px;
  }
`;

// ---------- рамка слів (лише показ: слова для вправи, напр. дієслова, які треба поставити у форму) ----------
export const WordBox = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${(props) => (props.$compact ? "6px" : "10px")};
  margin-bottom: ${(props) => (props.$compact ? "8px" : "16px")};
  padding: ${(props) => (props.$compact ? "6px 12px" : "12px 18px")};
  border-radius: ${radius.lg};
  border: 2px dashed ${colors.line};
  background: ${colors.surface};

  span {
    padding: ${(props) => (props.$compact ? "2px 10px" : "4px 14px")};
    border-radius: ${radius.pill};
    font-size: ${(props) => (props.$compact ? "17px" : "21px")};
    font-weight: 700;
    color: ${colors.brandDeep};
    background: ${colors.brandSoft};
  }
`;

// ---------- chat (переписка як у месенджері) ----------
const pop = keyframes`
  from { opacity: 0; transform: translateY(8px) scale(0.97); }
  to { opacity: 1; transform: translateY(0) scale(1); }
`;
const blink = keyframes`
  0%, 80%, 100% { opacity: 0.25; }
  40% { opacity: 1; }
`;
export const Chat = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 0;
  border-radius: ${radius.lg};
  background: #eef0f7;
  box-shadow: ${shadow.card};
  overflow: hidden;
`;
export const ChatHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 18px;
  font-size: 20px;
  color: #ffffff;
  background: ${colors.brand};

  button {
    border: none;
    background: transparent;
    color: #ffffff;
    font-size: 22px;
    cursor: pointer;
  }
`;
export const ChatBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 16px;
  overflow-y: auto;
`;
export const Bubble = styled.div`
  max-width: 78%;
  padding: 9px 14px;
  border-radius: 18px;
  border-bottom-${(props) => (props.$mine ? "right" : "left")}-radius: 4px;
  align-self: ${(props) => (props.$mine ? "flex-end" : "flex-start")};
  font-size: 18px;
  font-weight: 500;
  line-height: 1.4;
  color: ${(props) => (props.$mine ? "#ffffff" : colors.ink)};
  background: ${(props) => (props.$mine ? colors.brand : "#ffffff")};
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08);
  animation: ${pop} 0.25s ease-out;

  small {
    display: block;
    margin-bottom: 2px;
    font-size: 13px;
    font-weight: 800;
    color: ${colors.brandDeep};
  }
`;
export const Typing = styled.div`
  display: flex;
  gap: 4px;
  align-self: ${(props) => (props.$mine ? "flex-end" : "flex-start")};
  padding: 12px 14px;
  border-radius: 18px;
  background: ${(props) => (props.$mine ? colors.brandSoft : "#ffffff")};

  i {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: ${colors.muted};
    animation: ${blink} 1.2s infinite;
  }
  i:nth-child(2) { animation-delay: 0.2s; }
  i:nth-child(3) { animation-delay: 0.4s; }
`;
